<template>
  <div class="lp-LevelPage">
    <PageContentHeader
      :level="3"
      :max-width="HEADER_SIZES.LG"
      :title="$t('levels.header_title')"
      no-margin
      style="--align-items: flex-start"
    >
      <template v-if="!isNoLevels" #description>
        <i18n-t keypath="levels.page_description" scope="global">
          <template #action>
            <AppHrefLink href="https://oboard.atlassian.net/wiki/spaces/dlzlp/pages/1704198145">
              {{ $t('common.learn_about_use_cases') }}
            </AppHrefLink>
          </template>
        </i18n-t>
      </template>

      <DropdownMenu
        v-if="!isNoLevels"
        :disabled="isOkrLevelsLimitExceeded"
        :items="CREATE_LEVEL_DROPDOWN_ITEMS"
        :offset="[0, 0]"
        :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
        data-testid="add-user-dropdown"
        preferred-position="bottom-end"
        @item-click="handleAddAction"
      >
        <template #item-label="{ item }">
          <DropdownMenuItemLabelWithDescription :label-item="item" />
        </template>
        <template #activator>
          <AppButton
            v-tippy="{
              content: isOkrLevelsLimitExceeded
                ? okrLevelsLimitations.getDisabledButtonTooltip()
                : null
            }"
            :disable="isOkrLevelsLimitExceeded"
            class="lp-CreateButton"
            icon="plus-next"
            type="primary-next"
          >
            {{ $t('levels.title.create') }}
          </AppButton>
        </template>
      </DropdownMenu>
    </PageContentHeader>

    <div class="lp-MainContent">
      <template v-if="!isNoLevels">
        <TableSortable
          v-if="!isNoLevels"
          :columns="tableColumns"
          :data="levels"
          :hover-row="tableHoverRow"
          :loading="loading"
          class="lp-Table"
          offset-left="var(--page-left-padding)"
          offset-right="var(--page-right-padding)"
          sticky-header
          @drop="onTableRowDropped"
          @update:data="levels = $event"
        >
          <template #header-cell="{ column }">
            <template v-if="column.title">
              {{ $t(column.title) }}
            </template>
          </template>
          <template #cell="{ columnKey, item, index }">
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.NAME">
              <div class="lp-NameRow">
                <OkrIcon :objective="levelToOkrIconElement(item)" />
                <a class="lp-Name" @click.prevent="onEditLevel(item)"> {{ item.name }} </a>
              </div>
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.CATEGORY">
              {{
                $t(
                  getLevelCategoryName({
                    typeId: item[OKR_LEVEL_ENTITY_KEYS.TYPE_ID]
                  })
                )
              }}
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.WORKSPACE">
              <WorkspacesListCell :limit="3" :workspaces="item[OKR_LEVEL_ENTITY_KEYS.WORKSPACES]" />
            </template>
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.ACTIONS">
              <div class="lp-Actions">
                <DropdownMenu
                  :items="DROPDOWN_MENU_ACTIONS"
                  :offset="[0, 0]"
                  :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
                  preferred-position="bottom-end"
                  @close="tableHoverRow = -1"
                  @open="tableHoverRow = index"
                  @item-click="onMenuActionsClick($event, item)"
                >
                  <template #activator>
                    <AppButton icon="more-next" size="sm" type="tertiary-next" />
                  </template>
                </DropdownMenu>
              </div>
            </template>
          </template>
          <template #loading>
            <LevelsTableLoader />
          </template>

          <template #footer>
            <AppInfoMessage
              v-if="isOkrLevelsLimitExceeded"
              :type="INFO_MESSAGE_TYPES.WARNING"
              class="lp-Message"
            >
              {{
                $t('limitations.entity.limit_exceeded', {
                  limit: okrLevelsLimitations.limit,
                  entity: $t('levels.header_title')
                })
              }}
            </AppInfoMessage>

            <DropdownMenu
              v-else
              :items="CREATE_LEVEL_DROPDOWN_ITEMS"
              :offset="[0, 0]"
              :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
              follow-cursor="initial"
              preferred-position="bottom-start"
              @item-click="handleAddAction"
            >
              <template #item-label="{ item }">
                <DropdownMenuItemLabelWithDescription :label-item="item" />
              </template>
              <template #activator>
                <AppTableCreateButton full-width icon-name="plus-next">
                  {{ $t('levels.title.create') }}
                </AppTableCreateButton>
              </template>
            </DropdownMenu>
          </template>
        </TableSortable>

        <LevelsTemplates
          v-if="!loading"
          :created-levels="levels"
          :is-limit-exceeded="isOkrLevelsLimitExceeded"
          @create="onCreateLevelFromTemplate"
        />
      </template>
    </div>

    <SettingsPagesEmptyState
      v-if="isNoLevels"
      hero-height="128"
      hero-icon="no-levels-hero"
      hero-width="142"
    >
      <template #title> {{ $t('levels.no_levels') }} </template>
      <template #subtitle> {{ $t('levels.no_levels_subtitle') }} </template>

      <template #action>
        <AppButton
          height="24"
          icon="plus-next"
          type="primary-next"
          width="24"
          @click="onCreateLevelClick"
        >
          {{ $t('levels.title.create') }}
        </AppButton>
      </template>
    </SettingsPagesEmptyState>

    <ManageLevelModal
      :level="levelToEdit"
      :opened="showManageLevelModal"
      @on-close="onManageLevelModalClose"
    />

    <LevelModal
      v-model:show="showLevelModal"
      :level-template="levelTemplate"
      :model-value="levelToEdit"
      :tracking-source="
        levelTemplate?.[OKR_LEVEL_ENTITY_KEYS.PREFIX]
          ? EVENT_SOURCES.TEMPLATE
          : EVENT_SOURCES.BUTTON
      "
      @create="onLevelCreated"
      @edit="onLevelUpdated"
      @update:show="onUpdateShowLevelModal"
    />

    <AppDeleteModal
      :show="showDeleteLevelModal"
      data-auto-testid="delete-level-modal"
      data-testid="delete-level-modal"
      :keyword="KEYWORD"
      :title="$t('levels.delete_title', { levelName: levelToDelete?.name })"
      keypath="levels.delete_description"
      @on-close="hideDeleteLevelModal"
      @on-confirm="removeLevel"
    >
      <template #i18n-t:typeName>
        {{ levelToDelete?.name }}
      </template>
    </AppDeleteModal>

    <LevelReassignModal
      v-model:show="showReassignModal"
      :conflicts="conflictItems"
      :level="levelToDelete"
      :levels="levels"
      @reassigned="onReassigned"
    />
    <!--       :levels="levels" -->
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import { defineComponent } from 'vue'
import { mapActions } from 'vuex'

import LevelApiHandler from '@/api/level'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES, EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { INFO_MESSAGE_TYPES } from '@/utils/components-configurations/app-info-message'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import { MENU_ITEMS_GROUPS } from '@/utils/dropdown-menu'
import { OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { KEYWORD } from '@/utils/helpers'
import { levelToOkrIconElement, OKR_ELEMENTS_TABLE_ACTIONS_MENU_ITEMS } from '@/utils/objectives'
import { ELEMENT_TYPES_CATEGORIES, getLevelCategoryName } from '@/utils/okr-levels'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'

import AppHrefLink from '@/components/AppHrefLink'
import OkrIcon from '@/components/objectives/items/OkrIcon'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppInfoMessage from '@/components/ui/AppInfoMessage'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import DropdownMenuItemLabelWithDescription from '@/components/ui/DropdownMenu/DropdownMenuItemLabelWithDescription'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import LevelsTableLoader from '@/components/ui/SkeletonLoaders/LevelsTableLoader'
import TableSortable from '@/components/ui/Table/TableSortable/TableSortable'
import WorkspacesListCell from '@/components/ui/WorkspacesListCell/WorkspacesListCell'
import LevelModal from '@/components/workspaces/LevelModal'
import LevelReassignModal from '@/components/workspaces/LevelReassignModal'
import ManageLevelModal from '@/components/workspaces/ManageLevelModal'
import LevelsTemplates from '@/views/workspaces/settings/LevelsTemplates'

const CREATE_LEVEL_DROPDOWN_ITEMS = [
  {
    name: ACTIONS_KEYS.CREATE_OBJECTIVE,
    title: 'okr_elements.objective',
    icon: 'team-next',
    description: 'element_types.goal.dropdown.description',
    align: 'flex-start'
  },
  {
    name: ACTIONS_KEYS.CREATE_METRIC,
    title: 'okr_elements.metric',
    icon: 'metric-level',
    description: 'element_types.metric.dropdown.description',
    align: 'flex-start'
  }
]

const levelApi = new LevelApiHandler()

const { REMOVE, EDIT } = OKR_ELEMENTS_TABLE_ACTIONS_MENU_ITEMS

const DROPDOWN_MENU_ACTIONS = [
  {
    name: EDIT.name,
    title: 'action.edit',
    icon: 'edit-next',
    group: MENU_ITEMS_GROUPS.EDITING
  },
  {
    name: REMOVE.name,
    title: 'action.delete',
    icon: 'delete-next',
    color: 'var(--grade-low-color-next)',
    group: MENU_ITEMS_GROUPS.REMOVING
  }
]

export default defineComponent({
  name: 'LevelsPage',

  components: {
    AppDeleteModal,
    DropdownMenuItemLabelWithDescription,
    LevelsTemplates,
    LevelReassignModal,
    AppHrefLink,
    ManageLevelModal,
    WorkspacesListCell,
    AppInfoMessage,
    SettingsPagesEmptyState,
    AppTableCreateButton,
    DropdownMenu,
    LevelsTableLoader,
    AppButton,
    TableSortable,
    PageContentHeader,
    OkrIcon,
    LevelModal
  },

  setup() {
    const { okrLevelsLimitations } = usePlansLimitations()

    return {
      okrLevelsLimitations
    }
  },

  data() {
    return {
      loading: false,
      tableColumns: [
        {
          title: 'levels.name',
          key: TABLE_COLUMNS_KEYS.NAME,
          width: 'auto'
        },
        {
          title: 'levels.category',
          key: TABLE_COLUMNS_KEYS.CATEGORY,
          width: 244
        },
        {
          title: 'workspaces.workspaces',
          key: TABLE_COLUMNS_KEYS.WORKSPACE,
          width: 134
        },

        {
          key: TABLE_COLUMNS_KEYS.ACTIONS,
          width: 24
        }
      ],

      tableHoverRow: -1,
      levels: [],
      showLevelModal: false,
      levelTemplate: null,
      showManageLevelModal: false,
      showDeleteLevelModal: false,
      levelToEdit: null,
      levelToDelete: null,
      showReassignModal: false,
      conflictItems: []
    }
  },

  computed: {
    KEYWORD: () => KEYWORD,

    EVENT_SOURCES: () => EVENT_SOURCES,

    CREATE_LEVEL_DROPDOWN_ITEMS: () => CREATE_LEVEL_DROPDOWN_ITEMS,

    DROPDOWN_MENU_TYPES: () => DROPDOWN_MENU_TYPES,

    HEADER_SIZES: () => HEADER_SIZES,

    OKR_LEVEL_ENTITY_KEYS: () => OKR_LEVEL_ENTITY_KEYS,

    INFO_MESSAGE_TYPES: () => INFO_MESSAGE_TYPES,

    TABLE_COLUMNS_KEYS: () => TABLE_COLUMNS_KEYS,

    DROPDOWN_MENU_ACTIONS: () => DROPDOWN_MENU_ACTIONS,

    isNoLevels() {
      return isEmpty(this.levels) && !this.loading
    },

    isOkrLevelsLimitExceeded() {
      return this.okrLevelsLimitations.isLimitExceeded({
        count: this.levels.length
      })
    }
  },

  mounted() {
    this.getLevels()
  },

  methods: {
    getLevelCategoryName,

    ...mapActions('objectives', {
      setLevels: 'setLevels'
    }),

    levelToOkrIconElement,

    handleAddAction(action) {
      const template = {}

      if (action === ACTIONS_KEYS.CREATE_OBJECTIVE) {
        template[OKR_LEVEL_ENTITY_KEYS.TYPE_ID] = ELEMENT_TYPES_CATEGORIES.OBJECTIVE.typeId
      }

      if (action === ACTIONS_KEYS.CREATE_METRIC) {
        template[OKR_LEVEL_ENTITY_KEYS.TYPE_ID] = ELEMENT_TYPES_CATEGORIES.METRIC.typeId
      }

      this.onCreateLevelFromTemplate(template)
    },

    onMenuActionsClick(name, item) {
      if (name === EDIT.name) {
        this.onEditLevel(item)
      } else if (name === REMOVE.name) {
        this.checkDeleteLevel(item)

        tracker.logEvent('Started level removal', {
          category: EVENT_CATEGORIES.LEVEL_MANAGEMENT,
          label: item.name
        })
      }
    },

    onEditLevel(level) {
      this.levelToEdit = level
      this.showManageLevelModal = true
    },

    onLevelCreated() {
      this.showLevelModal = false
      this.updateSystemLevels()
    },

    onLevelUpdated() {
      this.showLevelModal = false
      this.showManageLevelModal = false
      this.updateSystemLevels()
    },

    onUpdateShowLevelModal(show) {
      if (!show) {
        this.levelTemplate = null
        this.levelToEdit = null
      }
    },

    onManageLevelModalClose({ reloadLevels = true } = {}) {
      this.showManageLevelModal = false
      this.showLevelModal = false
      this.levelToEdit = null
      if (reloadLevels) {
        this.updateSystemLevels()
      }
    },

    async getLevels() {
      this.loading = true
      try {
        this.levels = await levelApi.getLevels({
          workspaceIds: null,
          levelIds: null
        })
      } catch (error) {
        handleError({ error })
      } finally {
        this.loading = false
      }
    },

    async updateSystemLevels() {
      this.levels = []
      await this.getLevels()
      this.setLevels({
        levels: this.levels,
        updateFullLevelsList: true
      })
    },

    async checkDeleteLevel(item) {
      try {
        const conflicts = await levelApi.checkDeleteLevel({
          levelId: item.id
        })
        this.levelToDelete = item

        if (isEmpty(conflicts)) {
          this.showDeleteLevelModal = true
        } else {
          this.conflictItems = conflicts
          this.showReassignModal = true
        }
      } catch (error) {
        handleError({ error })
      }
    },

    onReassigned(item) {
      this.checkDeleteLevel(item)
    },

    async removeLevel() {
      try {
        await levelApi.removeLevel({
          levelId: this.levelToDelete.id
        })

        tracker.logEvent('Level removed', {
          category: EVENT_CATEGORIES.LEVEL_MANAGEMENT,
          label: this.levelToDelete.name
        })

        this.hideDeleteLevelModal()
        this.updateSystemLevels()
      } catch (error) {
        handleError({ error })

        tracker.logEvent('Level removed failed', {
          category: EVENT_CATEGORIES.LEVEL_MANAGEMENT,
          label: this.levelToDelete.name
        })
      }
    },

    // firstItems(item) {
    //   return item.workspaces
    //     .slice(0, 2)
    //     .map(i => i.name)
    //     .join(', ')
    // },
    //
    // lastItems(item) {
    //   return item.workspaces
    //     .slice(2)
    //     .map(i => i.name)
    //     .join(', ')
    // },
    //
    // lastItemsCount(item) {
    //   return item.workspaces.slice(2).length
    // },

    onCreateLevelClick() {
      if (!this.isOkrLevelsLimitExceeded) {
        this.showLevelModal = true
      }
    },

    onCreateLevelFromTemplate(template) {
      if (!this.isOkrLevelsLimitExceeded) {
        this.levelTemplate = template
        this.showLevelModal = true
      }
    },

    hideDeleteLevelModal() {
      this.showDeleteLevelModal = false
      this.levelToDelete = null
    },

    async onTableRowDropped({ item, orderValue }) {
      await levelApi.updateLevel({
        ...item,
        levelId: item.id,
        orderValue,
        workspaceIds: null
      })
      this.updateSystemLevels()
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/app-table-helpers';

.lp-LevelPage {
  padding-bottom: $page-bottom-padding;
  --description-max-width: 690px;
}

.lp-MainContent {
  width: 100%;
  max-width: $page-width-lg;
  padding-left: $page-left-padding;
  padding-right: $page-right-padding;
  font-family: $system-ui;
  margin-top: 7px;
}

.lp-NameRow {
  display: flex;
  align-items: center;
}

.lp-Name {
  color: $primary-color-next;
  margin: 0 8px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: $fs-14;
  line-height: 20px;
  font-weight: fw('semi-bold');
}

// .lp-LastItemsCount {
//   cursor: pointer;
// }

.lp-Actions {
  @extend %sortable-table-hidden-items;
}

.lp-Table {
  --head-padding-top: 16px;
}

.lp-Table {
  --head-padding-top: 16px;
}

.lp-Message {
  margin-top: 10px;
  padding-left: $page-left-padding;
}

.lp-CreateButton {
  pointer-events: all;
}
</style>
